import React from 'react';
import { Eye } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/Card';
import ProfileCard, { ProfileDisplayData } from '../../../components/ProfileCard';
import type { FormData, CurrentUser } from '../types';
import type {
  CompanyProfileSettingsView,
  ProfilePersonaView,
  PublicProfileDetailView,
} from '../../../api/PublicProfile/publicProfileApi';

interface LivePreviewProps {
  formData: FormData;
  currentUser: CurrentUser;
  profileUrl: string;
  companyBranding: CompanyProfileSettingsView | null;
  selectedPersonaData?: ProfilePersonaView;
  /**
   * The exact same payload the public `/p/:slug` page renders. When present,
   * it's used as the source of truth for everything (skills, achievements,
   * branding, contact, etc.) and the form fields overlay only the values the
   * user is actively editing — so the preview matches the live page 1:1
   * even for data the configuration form doesn't manage (e.g. UserSkills).
   */
  publicProfileDetail: PublicProfileDetailView | null;
  t: (key: string) => string;
}

/**
 * Build a persona-merged branding view as a fallback when the published
 * detail isn't available yet (draft profiles). Mirrors the server merge in
 * `PublicProfileService.GetFullProfileBySlug`.
 */
function buildFallbackBranding(
  companyBranding: CompanyProfileSettingsView | null,
  persona: ProfilePersonaView | undefined,
): ProfileDisplayData['branding'] {
  return {
    primaryColor: persona?.brandingPrimaryColor || companyBranding?.primaryColor || '#3B82F6',
    secondaryColor: persona?.brandingSecondaryColor || companyBranding?.secondaryColor || '#6366F1',
    disclaimerText: companyBranding?.disclaimerText,
    footerText: companyBranding?.footerText,
    showDisclaimer: companyBranding?.showDisclaimer ?? true,
  };
}

const LivePreview: React.FC<LivePreviewProps> = ({
  formData,
  currentUser,
  profileUrl,
  companyBranding,
  selectedPersonaData,
  publicProfileDetail,
  t
}) => {
  // Source-of-truth selection: the published detail wins because that's
  // exactly what visitors see on /p/:slug. Form values overlay only those
  // fields the configuration form actually edits — so unsaved bio / job
  // title / visibility tweaks show up immediately in the preview.
  const detail = publicProfileDetail;

  // Persona-aware logo: the published detail already has the persona-merged
  // branding logo applied (and respects `showCompanyLogo`). For drafts, fall
  // back to persona override → company default → user's company logo.
  const showLogo = detail
    ? (detail.branding?.showCompanyLogo ?? true)
    : (companyBranding?.showCompanyLogo ?? true);
  const detailLogo = detail?.branding?.companyLogoUrl;
  const personaLogo = selectedPersonaData?.brandingLogoUrl;
  const companyLogo = detailLogo
    ?? personaLogo
    ?? (showLogo ? companyBranding?.companyLogoUrl : null)
    ?? currentUser.companyLogo
    ?? null;

  const branding: ProfileDisplayData['branding'] = detail?.branding
    ? {
        primaryColor: detail.branding.primaryColor,
        secondaryColor: detail.branding.secondaryColor,
        disclaimerText: detail.branding.disclaimerText,
        footerText: detail.branding.footerText,
        showDisclaimer: detail.branding.showDisclaimer,
      }
    : buildFallbackBranding(companyBranding, selectedPersonaData);

  // Skills: real UserSkills from the published detail when available, else
  // the parser fallback for the legacy comma-separated form input. This is
  // why the preview previously showed no skills — `formData.skills` is a
  // string the form doesn't currently populate.
  const skills = (detail?.showSkills && detail.skills?.length)
    ? detail.skills.map(s => s.skillName)
    : (formData.skills?.split(',')?.map(s => s.trim()).filter(Boolean) ?? []);

  // Achievements: prefer the live form list (so newly added/edited entries
  // appear immediately), but if the form list is empty fall back to the
  // published detail.
  const achievements = formData.achievements.length > 0
    ? formData.achievements.map(a => ({
        id: a.id,
        title: a.title,
        date: a.date,
        description: a.description,
        displayOrder: a.displayOrder,
      }))
    : (detail?.achievements ?? []).map(a => ({
        id: a.id,
        title: a.title,
        date: a.date ?? '',
        description: a.description,
        displayOrder: a.displayOrder,
      }));

  // Social links: same overlay logic.
  const socialLinks = formData.socialLinks.length > 0
    ? formData.socialLinks.map(s => ({ platform: s.platform, url: s.url, icon: s.iconName }))
    : (detail?.socialLinks ?? []).map(s => ({ platform: s.platform, url: s.url, icon: s.iconName }));

  // Form fields overlay onto detail — that way the preview reflects unsaved
  // edits, but unedited fields fall back to the live published values.
  const profileDisplayData: ProfileDisplayData = {
    firstName: detail?.user?.firstName ?? currentUser.firstName,
    lastName: detail?.user?.lastName ?? currentUser.lastName,
    fullName: detail?.user?.fullName ?? currentUser.fullName ?? `${currentUser.firstName} ${currentUser.lastName}`,
    jobTitle: formData.jobTitle || detail?.user?.title || detail?.customJobTitle || currentUser.title,
    department: detail?.user?.departmentName ?? currentUser.department,
    location: formData.location || detail?.customLocation || '',
    bio: formData.bio || detail?.bio || '',
    email: formData.emailOverride || detail?.user?.email || currentUser.email,
    phone: formData.phoneOverride || detail?.user?.phoneNumber || currentUser.phone,
    photo: detail?.user?.imageUrl ?? currentUser.imageUrl,
    companyName: formData.companyNameOverride || detail?.company?.name || currentUser.companyName,
    companyLogo,
    skills,
    achievements,
    socialLinks,
    showEmail: formData.showEmail,
    showPhone: formData.showPhone,
    showSkills: formData.showSkills,
    showAchievements: formData.showAchievements,
    colorScheme: formData.colorScheme,
    profileUrl,
    slug: formData.publicSlug || detail?.slug,
    branding,
  };

  return (
    <div className="lg:sticky lg:top-32 h-fit">
      <Card>
        <CardContent className="p-4">
          <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Eye className="w-5 h-5 me-2" />
            {t('Live Preview')}
          </h2>

          <ProfileCard
            data={profileDisplayData}
            variant="preview"
            showQRCode={true}
            t={t}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default LivePreview;
